/**
 * PortalChooser — post-auth multi-portal selection card (Tier 0).
 *
 * Per `.design-incoming/auth-admin-20260522/PORTAL_CHOOSER_SPEC.md`
 * + `COMPONENT_API.md §PortalChooser`. Tier 0 module-local —
 * `/portal` (rendered by `PortalPage`) is the only consumer; per
 * ADR `frontend-0004` no cross-slice consumer trigger fires.
 *
 * Composes kit `BrandMark` + `Button`. Tiles are hand-rolled
 * `<button role="radio">` because the kit has no radiogroup
 * primitive yet (and per ADR `frontend-0004` no second-consumer
 * trigger justifies promoting one — this is the only chooser
 * surface in the app). Tile geometry, color tokens, and radio
 * indicator follow `PORTAL_CHOOSER_SPEC.md §2` exactly.
 *
 * Last-used pill: rendered as `<Badge variant="neutral" size="sm">`
 * per bundle spec. The kit Badge (SCRUM-115, PR #143) is on main as
 * of merge `0521a46`; this surface consumes it directly. Pass 2 swap
 * landed in Sergey round-2 (2026-05-28).
 *
 * Behavior contract per `PORTAL_CHOOSER_SPEC.md §3`:
 *   - Click tile         → sets active selection (no submit)
 *   - Double-click tile  → sets active + fires `onSelect(id)`
 *   - Enter on focused   → same as double-click
 *   - Arrow up/down      → move active selection (radiogroup nav)
 *   - Continue button    → fires `onSelect(activeId)`
 *   - Sign-out link      → fires `onSignOut()`
 *   - Esc                → no-op (chooser is non-dismissible)
 *
 * Accessibility per `ACCESSIBILITY.md §1-2`:
 *   - `<main aria-labelledby="chooser-heading">` wraps the surface
 *   - Tiles live in `<div role="radiogroup" aria-label="Choose portal">`
 *   - Each tile is `<button role="radio" aria-checked={active}>`
 *   - Roving tabindex (only the active tile is tab-focusable)
 *   - Arrow keys handle intra-group navigation per WAI-ARIA
 */

import * as React from 'react';
import { Badge, BrandMark, Button, Icon } from '@sparta/ui-kit';
import {
  type PortalId,
  type PortalIconKey,
  portalDesc,
  portalIcon,
  portalTitle,
} from '../../../shared/auth/portal-id.js';

export interface PortalChooserUser {
  readonly name: string;
  readonly email: string;
  readonly avatar_url?: string;
}

export interface PortalChooserTile {
  readonly id: PortalId;
  readonly context: {
    readonly primary: string;
    readonly secondary?: string;
  };
  readonly lastUsed?: boolean;
}

export interface PortalChooserProps {
  readonly user: PortalChooserUser;
  /** Length must be > 1; host's responsibility to guard. */
  readonly portals: readonly PortalChooserTile[];
  readonly onSelect: (id: PortalId) => void;
  readonly onSignOut: () => void;
  /** Mobile compact layout (< 900 px). Reduces padding + heading scale. */
  readonly compact?: boolean;
}

/** Splits the user's name to extract the first token; falls back to "there". */
function firstName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'there';
  const first = trimmed.split(/\s+/, 1)[0];
  return first === undefined || first.length === 0 ? 'there' : first;
}

/**
 * Portal glyph — dispatches to the kit `Icon` dictionary (SCRUM-199).
 * `icon` is one of 'home2' | 'swap' | 'shield', all valid `IconName`s.
 */
function PortalGlyph({ icon }: { icon: PortalIconKey }): React.ReactElement {
  return <Icon name={icon} size={24} />;
}

/**
 * Radio check glyph used inside the active tile's selection indicator.
 * White checkmark via the kit `Icon` (currentColor); the enclosing
 * selection-indicator span sets `text-white` so currentColor resolves
 * to white without a hardcoded hex (design-token lint forbids hex).
 */
function CheckGlyph(): React.ReactElement {
  return <Icon name="check" size={12} strokeWidth={3} />;
}

const ARROW_RIGHT = <Icon name="arrowRight" size={18} />;

export function PortalChooser({
  user,
  portals,
  onSelect,
  onSignOut,
  compact = false,
}: PortalChooserProps): React.ReactElement {
  // Default active = the `lastUsed` tile, falling back to portals[0].
  // The spec (§3 "Behavior contract") locks this in.
  const initialActiveId = React.useMemo<PortalId>(() => {
    const lastUsed = portals.find((p) => p.lastUsed === true);
    if (lastUsed !== undefined) return lastUsed.id;
    const head = portals[0];
    if (head === undefined) {
      // Host guarantees portals.length > 1; this branch exists only
      // for the type narrowing. PortalPage refuses to mount the
      // chooser if the array is short.
      throw new Error('PortalChooser: portals must not be empty');
    }
    return head.id;
  }, [portals]);

  const [activeId, setActiveId] = React.useState<PortalId>(initialActiveId);
  const tileRefs = React.useRef<Map<PortalId, HTMLButtonElement | null>>(
    new Map(),
  );

  // Sync active when the portals prop or the resolved default changes.
  // Avoids stale-active during HMR or when the host swaps the list.
  React.useEffect(() => {
    setActiveId(initialActiveId);
  }, [initialActiveId]);

  const setTileRef = React.useCallback(
    (id: PortalId, el: HTMLButtonElement | null) => {
      tileRefs.current.set(id, el);
    },
    [],
  );

  const focusTile = React.useCallback((id: PortalId) => {
    // Defer focus to the next paint so the new tabindex=0 node is
    // reachable. Mirrors the SCRUM-57 LoginPage portal-radio pattern
    // (since removed in SCRUM-73 from the login surface, retained
    // here because the chooser is a real radiogroup).
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      tileRefs.current.get(id)?.focus();
    });
  }, []);

  const selectActive = React.useCallback(
    (next: PortalId, focusAfter: boolean) => {
      setActiveId(next);
      if (focusAfter) focusTile(next);
    },
    [focusTile],
  );

  const handleTileKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, current: PortalId) => {
      const idx = portals.findIndex((p) => p.id === current);
      if (idx < 0) return;
      let nextIdx: number | null = null;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIdx = (idx + 1) % portals.length;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIdx = (idx - 1 + portals.length) % portals.length;
      } else if (event.key === 'Home') {
        nextIdx = 0;
      } else if (event.key === 'End') {
        nextIdx = portals.length - 1;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        onSelect(current);
        return;
      } else if (event.key === ' ') {
        event.preventDefault();
        selectActive(current, false);
        return;
      } else if (event.key === 'Escape') {
        // Bundle §3: Esc is no-op. preventDefault to keep parent
        // dialogs/sheets from treating Esc as dismiss.
        event.preventDefault();
        return;
      }
      if (nextIdx !== null) {
        event.preventDefault();
        const target = portals[nextIdx];
        if (target !== undefined) selectActive(target.id, true);
      }
    },
    [portals, selectActive, onSelect],
  );

  const handleTileClick = React.useCallback(
    (id: PortalId) => {
      setActiveId(id);
    },
    [],
  );

  const handleTileDoubleClick = React.useCallback(
    (id: PortalId) => {
      setActiveId(id);
      onSelect(id);
    },
    [onSelect],
  );

  // ── Token / sizing values that depend on compact ────────────────
  const cardPadding = compact ? '24px' : '48px';
  const headingFontSize = compact ? '24px' : '28px';
  const tilePadding = compact ? '14px' : '16px';
  const greetingName = firstName(user.name);

  return (
    <main
      aria-labelledby="chooser-heading"
      className="flex min-h-screen w-full items-center justify-center"
      style={{
        background: 'var(--bg-canvas)',
        color: 'var(--fg-primary)',
        padding: compact ? '24px 16px' : '48px 24px',
      }}
    >
      <div
        className="w-full"
        style={{
          maxWidth: '520px',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          padding: cardPadding,
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--border-default)',
        }}
      >
        {/* Brand mark — centered, 16px below top per spec §1 */}
        <div className="mb-4 flex justify-center">
          <BrandMark size={36} />
        </div>

        {/* Heading */}
        <h1
          id="chooser-heading"
          className="text-center font-bold"
          style={{
            fontSize: headingFontSize,
            letterSpacing: '-0.6px',
            marginBottom: '8px',
          }}
        >
          Welcome back, {greetingName}.
        </h1>

        {/* Subtitle */}
        <p
          className="mx-auto mb-6 text-center text-sm"
          style={{ color: 'var(--fg-secondary)', maxWidth: '420px' }}
        >
          You have access to multiple portals. Choose where to go.
        </p>

        {/* Tile group */}
        <div
          role="radiogroup"
          aria-label="Choose portal"
          className="mb-5 flex flex-col"
          style={{ gap: '10px' }}
        >
          {portals.map((tile) => {
            const checked = tile.id === activeId;
            const title = portalTitle(tile.id);
            const desc = portalDesc(tile.id);
            const iconKey = portalIcon(tile.id);
            return (
              // SCRUM-113: kit Button does not support the WAI-ARIA
              // radio pattern (`role="radio"` + roving tabindex inside
              // a parent `radiogroup`). The chooser is the only
              // surface in the app using this pattern, so promoting a
              // `RadioTile` primitive to the kit isn't justified per
              // ADR `frontend-0004` (no cross-slice consumer trigger).
              // eslint-disable-next-line @sparta/no-hand-rolled-button
              <button
                key={tile.id}
                ref={(el) => setTileRef(tile.id, el)}
                type="button"
                role="radio"
                aria-checked={checked}
                /* Sergey round-2 MEDIUM 2 (2026-05-28): the round-1 `aria-label={title}`
                   override fully shadowed the descendant content — screen readers
                   announced only "Internal admin, radio button, checked," omitting
                   the description, context, and Last-used pill (SC 1.3.1 fail).
                   Compose the accessible name + description from real IDs:
                   - aria-labelledby → title (+ "Last used" when present)
                   - aria-describedby → desc + context line
                   This way AT users hear the same semantics sighted users see. */
                aria-labelledby={
                  tile.lastUsed === true
                    ? `portal-tile-title-${tile.id} portal-tile-lastused-${tile.id}`
                    : `portal-tile-title-${tile.id}`
                }
                aria-describedby={`portal-tile-desc-${tile.id} portal-tile-context-${tile.id}`}
                tabIndex={checked ? 0 : -1}
                onClick={() => handleTileClick(tile.id)}
                onDoubleClick={() => handleTileDoubleClick(tile.id)}
                onKeyDown={(e) => handleTileKeyDown(e, tile.id)}
                className="flex w-full items-center text-left transition-colors"
                style={{
                  gap: '14px',
                  padding: tilePadding,
                  borderWidth: '1.5px',
                  borderStyle: 'solid',
                  borderRadius: 'var(--radius-xl)',
                  background: checked
                    ? 'color-mix(in oklab, var(--brand-500) 8%, var(--bg-surface))'
                    : 'var(--bg-surface)',
                  borderColor: checked
                    ? 'var(--brand-500)'
                    : 'var(--border-default)',
                  minHeight: '84px',
                  cursor: 'pointer',
                }}
              >
                {/* Tile icon — 44×44, brand bg when active */}
                <span
                  aria-hidden="true"
                  className="flex flex-shrink-0 items-center justify-center"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: 'var(--radius-lg)',
                    background: checked
                      ? 'var(--brand-500)'
                      : 'var(--bg-sunken)',
                    color: checked ? '#ffffff' : 'var(--fg-secondary)',
                  }}
                >
                  <PortalGlyph icon={iconKey} />
                </span>

                {/* Tile body */}
                <span className="min-w-0 flex-1">
                  <span
                    className="flex items-center"
                    style={{ gap: '8px' }}
                  >
                    <span
                      id={`portal-tile-title-${tile.id}`}
                      className="font-bold"
                      style={{
                        fontSize: '15px',
                        color: checked
                          ? 'var(--brand-700)'
                          : 'var(--fg-primary)',
                      }}
                    >
                      {title}
                    </span>
                    {tile.lastUsed === true ? (
                      <span id={`portal-tile-lastused-${tile.id}`}>
                        <Badge variant="neutral" size="sm">Last used</Badge>
                      </span>
                    ) : null}
                  </span>
                  <span
                    id={`portal-tile-desc-${tile.id}`}
                    className="block"
                    style={{
                      fontSize: '12px',
                      color: 'var(--fg-tertiary)',
                      marginTop: '2px',
                    }}
                  >
                    {desc}
                  </span>
                  <span
                    id={`portal-tile-context-${tile.id}`}
                    className="block font-medium"
                    style={{
                      fontSize: '12px',
                      color: 'var(--fg-secondary)',
                      marginTop: '2px',
                    }}
                  >
                    {tile.context.secondary !== undefined
                      ? `${tile.context.primary} · ${tile.context.secondary}`
                      : tile.context.primary}
                  </span>
                </span>

                {/* Radio indicator — `text-white` makes the inner
                    CheckGlyph (kit Icon, currentColor) resolve white
                    against the brand-500 fill when checked, without a
                    hardcoded hex (design-token lint). */}
                <span
                  aria-hidden="true"
                  className="flex flex-shrink-0 items-center justify-center text-white"
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--radius-full)',
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    background: checked ? 'var(--brand-500)' : 'transparent',
                    borderColor: checked
                      ? 'var(--brand-500)'
                      : 'var(--border-default)',
                  }}
                >
                  {checked ? <CheckGlyph /> : null}
                </span>
              </button>
            );
          })}
        </div>

        {/* Primary submit — full-width, large */}
        <Button
          type="button"
          variant="primary"
          size="lg"
          full
          onClick={() => onSelect(activeId)}
        >
          <span>Continue to {activeId}</span>
          {ARROW_RIGHT}
        </Button>

        {/* Sign-out link — PORTAL_CHOOSER_SPEC.md §1 explicitly
            describes this as a "tertiary link" (centered, beneath
            the primary button). Semantically it triggers an action
            (logout + navigate), so an interactive `<button>` is
            correct over `<a>`; visually it should read as link, so
            we don't compose the kit `Button` (none of its variants
            match link styling — closest is `ghost` which paints in
            `--fg-primary`, wrong color hierarchy for a tertiary). */}
        <div className="mt-4 flex justify-center">
          {/* eslint-disable-next-line @sparta/no-hand-rolled-button */}
          <button
            type="button"
            onClick={onSignOut}
            className="text-sm font-semibold underline-offset-2 hover:underline"
            style={{ color: 'var(--fg-brand)' }}
          >
            Sign in as a different user
          </button>
        </div>
      </div>
    </main>
  );
}
